import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { usePageTitle } from "../../hooks/usePageTitle";

interface Message {
  id: number;
  subject: string;
  message: string;
  customer_email: string;
  order_number: string | null;
  created_at: string;
  status: "new" | "read" | "replied";
  admin_reply: string | null;
}

export default function MessageDetail() {
  usePageTitle("Support Ticket");
  const { messageId } = useParams<{ messageId: string }>();
  const [message, setMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (messageId) {
      fetchMessageDetail();
    }
  }, [messageId]);

  const fetchMessageDetail = async () => {
    try {
      const response = await fetch(`/api/customer/messages/${messageId}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(data);

        // Mark as read if it has an admin reply and hasn't been read yet
        if (data.status === "replied") {
          await markAsRead();
        }
      } else if (response.status === 401) {
        setError("Please log in to view this message");
      } else if (response.status === 403) {
        setError("You don't have permission to view this message");
      } else {
        setError("Message not found");
      }
    } catch (error) {
      console.error("Error fetching message:", error);
      setError("An error occurred while loading the message");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await fetch(`/api/customer/messages/${messageId}/read`, {
        method: "PUT",
        credentials: "include",
      });
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded">New</span>;
      case "read":
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded">Read</span>;
      case "replied":
        return <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded">Replied</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">Loading message...</div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="card text-center">
          <p className="text-red-600 mb-4">{error || "Message not found"}</p>
          <Link to="/account" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Support Ticket</h1>
          <p className="text-gray-600">Ticket #{message.id}</p>
        </div>
        <Link to="/account" className="btn-secondary">
          Back to Dashboard
        </Link>
      </div>

      {/* Message Details Card */}
      <div className="card mb-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">{message.subject}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>{formatDate(message.created_at)}</span>
              {message.order_number && (
                <>
                  <span>•</span>
                  <Link
                    to={`/account/orders/${message.order_number}`}
                    className="text-black underline hover:no-underline"
                  >
                    Order #{message.order_number}
                  </Link>
                </>
              )}
            </div>
          </div>
          {getStatusBadge(message.status)}
        </div>

        {/* Original Message */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-bold text-gray-600">You</span>
            </div>
            <div>
              <p className="font-medium">Your Message</p>
              <p className="text-sm text-gray-600">{message.customer_email}</p>
            </div>
          </div>
          <div className="text-gray-800 whitespace-pre-wrap">
            {message.message}
          </div>
        </div>

        {/* Admin Reply */}
        {message.admin_reply && (
          <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-600">Admin</span>
              </div>
              <div>
                <p className="font-medium">Support Response</p>
                <p className="text-sm text-gray-600">Deb's Expressions Support Team</p>
              </div>
            </div>
            <div className="text-gray-800 whitespace-pre-wrap">
              {message.admin_reply}
            </div>
          </div>
        )}

        {/* No Reply Yet */}
        {!message.admin_reply && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-800">
              We're reviewing your message and will respond as soon as possible.
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="card bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold mb-1">Need More Help?</h3>
            <p className="text-sm text-gray-600">
              Submit a new inquiry or follow up on this ticket
            </p>
          </div>
          <Link
            to="/contact"
            state={{ orderNumber: message.order_number }}
            className="btn-primary"
          >
            Send Follow-up
          </Link>
        </div>
      </div>
    </div>
  );
}
